<template>
  <div class="historyrecord-container">
    <!-- 图表区域 -->
    <div class="chart-container">
      <div ref="chartRef" class="chart"></div>
    </div>

    <!-- 筛选条件和数据表格区域 -->
    <div class="control-panel">
      <!-- 筛选条件区域 -->
      <div class="filter-section">
        <el-form :inline="true" class="filter-row">
          <el-form-item :label="$t('message.reaTimePlot.companyDeviceTree')" class="filter-item">
            <el-cascader
              v-model="currentDeviceKey"
              :data="deviceList"
              :options="deviceTreeOptions"
              :props="deviceTreeProps"
              clearable
              :placeholder="$t('message.reaTimePlot.selectDevice')"
              @change="handleDeviceChange"
              class="filter-control"
              style="width: 400px;"
            ></el-cascader>
          </el-form-item>

          <el-form-item :label="$t('message.reaTimePlot.monitorVariable')" class="filter-item">
            <VariableSelector
              v-model="selectedVariables"
              :device-id="currentDeviceId"
              @change="handleVariableChange"
              class="filter-control variable-selector"              
            />
          </el-form-item>

          <el-form-item  class="filter-item time-range-item">
            <div class="time-range-group">
              <el-select 
                v-model="quickTime" 
                :placeholder="$t('message.historyRecord.selectTime')"
                @change="handleQuickTimeChange"
                class="quick-time-select"
              >
                <el-option
                  v-for="item in quickTimeOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>
              <el-date-picker
                v-model="timeRange"
                type="datetimerange"
                range-separator="-"
                :start-placeholder="$t('message.historyRecord.startTime')"
                :end-placeholder="$t('message.historyRecord.endTime')"
                value-format="YYYY-MM-DD HH:mm:ss"
                @change="handleTimeRangeChange"
                class="time-range-picker"
              />
            </div>
          </el-form-item>

          <div class="action-buttons">
            <el-button type="primary" @click="handleQuery" :loading="loading" class="action-btn">
              {{ $t("message.historyRecord.query") }}
            </el-button>
            <el-button @click="handleReset" :disabled="loading" class="action-btn">
              {{ $t("message.historyRecord.reset") }}
            </el-button>
            <el-button 
              type="primary" 
              :loading="exportLoading"
              @click="handleExport()"
              class="action-btn"
            >
              {{ $t('message.historyRecord.exportVariables') }}
            </el-button>
          </div>
        </el-form>
      </div>

      <!-- 数据列表区域 -->
      <div class="data-section">
        <div class="table-container">
          <el-table 
            :data="tableData" 
            border 
            v-loading="loading"
            class="data-table"
            :max-height="tableHeight"
          >
            <el-table-column
              prop="timestamp"
              :label="$t('message.reaTimePlot.time')"
              width="180"
              sortable
              fixed="left"
            ></el-table-column>
            <el-table-column
              v-for="variable in selectedVariables"
              :key="variable.name"
              :prop="variable.name"
              :label="variable.display_name"
              min-width="150"
              show-overflow-tooltip
            ></el-table-column>
          </el-table>
        </div>
        
        <!-- 分页控件 -->
        <div class="pagination-container" v-if="tableData.length > 0">
          <el-pagination
            v-model:current-page="pagination.currentPage"
            v-model:page-size="pagination.pageSize"
            :page-sizes="[100,500,1000,3000, 5000]"
            :total="pagination.total"
            layout="total, sizes, prev, pager, next, jumper"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            class="pagination"
          />
        </div>        
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  reactive,
  onMounted,
  onBeforeUnmount,
  nextTick,
  watch 
} from "vue";
import * as echarts from "echarts";
import { ElMessage } from "element-plus";
import { getCompanyDeviceTree } from "@/api/CompanyManager";
import { useI18n } from "vue-i18n";
import VariableSelector from "@/components/views/VariableSelector.vue";
//import { queryHistoryAllData,exportHisVariablesByFile,queryHistoryWhenAllData } from "@/api/DeviceManagement";
import { exportHisVariablesByFile,queryHistoryWhenAllData } from "@/api/DeviceManagement";
import pako from 'pako';
 
const { t } = useI18n();
const loading = ref(false);
const exportLoading = ref(false)
// 设备列表
const deviceList = ref([]);
// 设备树相关
const currentDeviceKey = ref(null);
const currentDeviceId = ref(null);
const selectedDevice = ref({});
const deviceTreeOptions = ref([]);
const deviceTreeProps = {
  value: "uniqueValue",
  label: "name",
  children: "children",
  checkStrictly: true,
  emitPath: false,
};

// 变量相关
const selectedVariables = ref([]);
// 时间范围选择
const timeRange = ref([]);
// 快速时间选择
const quickTime = ref('today'); // 默认选择今天
const quickTimeOptions = ref([]);

// 图表相关
const chartRef = ref(null);
let chartInstance = null;

// 表格数据
const tableData = ref([]);
 
// 缓存数据 - 用于存储所有查询到的历史数据
const cachedHistoryData = ref(new Map()); // 使用Map存储，key为时间戳，value为数据项

// 分页配置
const pagination = reactive({
  currentPage: 1,
  pageSize: 100,
  total: 0,
});



// 生成存储键名
const getStorageKey = () => {
  const deviceId = currentDeviceId.value || 'no_device';
  const variablesKey = selectedVariables.value.length > 0 
    ? selectedVariables.value.map(v => v.name).join('_') 
    : 'no_variables';
  const timeRangeKey = timeRange.value.length === 2 
    ? `${timeRange.value[0]}_${timeRange.value[1]}`.replace(/[:-\s]/g, '')
    : 'no_time';
  
  return `HistoryAllInfo_selectedVariables_${deviceId}_${variablesKey}_${timeRangeKey}`;
};

// 通用的日期格式化函数
const formatLocalDateTime = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

// 保存选择信息到本地存储
const saveSelectionToStorage = () => {
  try {
    const storageKey = getStorageKey();
    const selectionData = {
      deviceId: currentDeviceId.value,
      variables: selectedVariables.value,
      timeRange: timeRange.value,
      quickTime: quickTime.value,
      saveTime: new Date().toISOString()
    };
    
    localStorage.setItem(storageKey, JSON.stringify(selectionData));
    console.log('选择信息已保存，键名:', storageKey);
  } catch (error) {
    console.error('保存选择信息失败:', error);
  }
};

// 从本地存储加载选择信息
const loadSelectionFromStorage = () => {
  try {
    // 尝试获取所有相关的存储键
    const allKeys = Object.keys(localStorage);
    const historyKeys = allKeys.filter(key => key.startsWith('HistoryAllInfo_selectedVariables_'));
    
    if (historyKeys.length === 0) {
      console.log('没有找到保存的选择信息');
      return;
    }
    
    // 按保存时间排序，获取最新的一个
    let latestKey = null;
    let latestTime = null;
    let savedData = null;
    
    for (const key of historyKeys) {
      try {
        const data = JSON.parse(localStorage.getItem(key));
        if (data && data.saveTime) {
          const saveTime = new Date(data.saveTime);
          if (!latestTime || saveTime > latestTime) {
            latestTime = saveTime;
            latestKey = key;
            savedData = data;
          }
        }
      } catch (e) {
        console.warn('解析存储数据失败:', key, e);
      }
    }
    
    if (savedData) {
      console.log('找到保存的选择信息:', savedData);
      
      // 先设置基本数据
      if (Array.isArray(savedData.variables) && savedData.variables.length > 0) {
        selectedVariables.value = savedData.variables;
        console.log('加载保存的变量:', savedData.variables);
      }
      
      if (Array.isArray(savedData.timeRange) && savedData.timeRange.length === 2) {
        timeRange.value = savedData.timeRange;
        console.log('加载保存的时间范围:', savedData.timeRange);
      }
      
      if (savedData.quickTime) {
        quickTime.value = savedData.quickTime;
        console.log('加载保存的快速时间:', savedData.quickTime);
      }
      
      // 设备ID需要特殊处理，等待设备树加载完成
      if (savedData.deviceId) {
        console.log('准备加载设备ID:', savedData.deviceId);
        currentDeviceId.value = savedData.deviceId;
        
        // 如果设备树已经加载，立即设置设备
        if (deviceTreeOptions.value && deviceTreeOptions.value.length > 0) {
          setDeviceFromStorage(savedData.deviceId);
        }
      }
      
      console.log('从存储键加载选择信息完成:', latestKey);
    }
  } catch (error) {
    console.error('加载选择信息失败:', error);
    // 如果加载失败，使用默认值
    initializeTimeRange();
  }
};

// 专门处理从存储设置设备
const setDeviceFromStorage = async(deviceId) => {
  console.log('开始设置存储设备:', deviceId);
  
  const findDeviceNode = (nodes, id) => {
    for (const node of nodes) {
      console.log('检查节点:', node.id, node.name, node.type);
      
      if (node.id === id && node.type === 2) {        
        //ElMessage.success(`选中的设备: ${node.name}-${node.type}`);
        console.log('找到匹配的设备节点:', node);
        return node;
      }
      if (node.children && node.children.length > 0) {
        const found = findDeviceNode(node.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  
   //ElMessage.success(`设备ID: ${deviceId}`);
  const selectedNode = findDeviceNode(deviceTreeOptions.value, deviceId);
  console.log('查找设备节点结果:', selectedNode);

  if (selectedNode) {
    selectedDevice.value = selectedNode;
    currentDeviceId.value = selectedNode.id;
    currentDeviceKey.value = selectedNode.uniqueValue;
    console.log('成功设置设备:', selectedNode.name);        
    // 关键修复：等待下一个tick确保DOM更新，然后重新设置设备选择器的值
    await nextTick();    
    // 强制更新设备选择器的显示
    currentDeviceId.value = selectedNode.id;
    currentDeviceKey.value = selectedNode.uniqueValue;
    // 显示成功消息
    //setTimeout(() => {  ElMessage.success(`已自动加载上次选择的设备: ${selectedNode.name}`);    }, 500);
  } else {
    console.warn('未找到对应的设备节点，设备ID:', deviceId);
    //ElMessage.warning('上次选择的设备不存在，请重新选择');
    currentDeviceId.value = null;
  }
};

// 清空保存的选择信息
const clearSavedSelection = () => {
  try {
    // 获取所有相关的存储键并清除
    const allKeys = Object.keys(localStorage);
    const historyKeys = allKeys.filter(key => key.startsWith('HistoryAllInfo_selectedVariables_'));
    
    historyKeys.forEach(key => {
      localStorage.removeItem(key);
    });
    
    console.log('已清空所有保存的选择信息，数量:', historyKeys.length);
  } catch (error) {
    console.error('清空保存信息失败:', error);
  }
};

// 清理过期的存储数据（保留最近5个）
const cleanupExpiredStorage = () => {
  try {
    const allKeys = Object.keys(localStorage);
    const historyKeys = allKeys.filter(key => key.startsWith('HistoryAllInfo_selectedVariables_'));
    
    if (historyKeys.length > 5) {
      // 按保存时间排序
      const keysWithTime = historyKeys.map(key => {
        try {
          const data = JSON.parse(localStorage.getItem(key));
          return {
            key,
            time: data?.saveTime ? new Date(data.saveTime) : new Date(0)
          };
        } catch {
          return { key, time: new Date(0) };
        }
      }).sort((a, b) => b.time - a.time);
      
      // 保留最近5个，删除其他的
      const keysToRemove = keysWithTime.slice(5);
      keysToRemove.forEach(item => {
        localStorage.removeItem(item.key);
      });
      
      console.log('清理过期存储数据，删除数量:', keysToRemove.length);
    }
  } catch (error) {
    console.error('清理存储数据失败:', error);
  }
};

const loadOptions = () => { 
   quickTimeOptions.value = [
     { value: 'today', label: t('message.historyRecord.today') },
     { value: 'yesterday', label: t('message.historyRecord.yesterday') },
     { value: 'custom', label: t('message.historyRecord.timeRange') }
   ];
};

const getTodayTimeRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
  
  return [
    formatLocalDateTime(start),
    formatLocalDateTime(end)
  ];
};

// 获取昨天的时间范围
const getYesterdayTimeRange = () => {
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  
  const start = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 0, 0, 0);
  const end = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 23, 59, 59);

  return [
    formatLocalDateTime(start),
    formatLocalDateTime(end)
  ];
};

// 初始化时间范围
const initializeTimeRange = () => {
  // 默认设置为今天的时间范围
  timeRange.value = getTodayTimeRange();
  quickTime.value = 'today';
};

// 处理快速时间选择
const handleQuickTimeChange = (value) => {
    ResetData();
  if (value === 'today') {
    timeRange.value = getTodayTimeRange();
  } else if (value === 'yesterday') {
    timeRange.value = getYesterdayTimeRange();
  } else if (value === 'custom') {
    // 自定义时间，清空时间范围让用户选择
    timeRange.value = [];
  }
};

// 处理时间范围变化
const handleTimeRangeChange = () => {
  // 当用户手动选择时间范围时，设置为自定义
  ResetData();
  quickTime.value = 'custom';
};

// // 获取变量名数组（用于内部处理）
// const getVariableNames = () => {
//   return selectedVariables.value.map(v => v.name);
// };

// 获取显示名数组（用于显示）
const getDisplayNames = () => {
  return selectedVariables.value.map(v => v.display_name);
};

// 初始化图表
const initChart = () => {
  if (chartRef.value) {
    chartInstance = echarts.init(chartRef.value);
    const option = {
      title: {
        text: t("message.reaTimePlot.deviceVariableMonitor"),
      },
      tooltip: {
        trigger: "axis",
      },
      legend: {
        data: [],
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "3%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: [],
      },
      yAxis: {
        type: "value",
      },
      series: [],
    };
    chartInstance.setOption(option);
  }
};

// 格式化设备树数据
const formatDeviceTree = (treeData) => {
  return treeData.map((item) => {
    const node = {
      id: item.id,
      name: item.name,
      type: item.type,
      key: item.key,
      deviceCode: item.data?.device_code || "",
      parentId: item.parentId,
      parentKey: item.parentKey,
      uniqueValue: `${item.id}_${item.type}`, // 添加唯一值字段
    };

    if (item.children && item.children.length > 0) {
      node.children = formatDeviceTree(item.children);
    }

    return node;
  });
};

// 加载设备树
const loadDeviceTree = async () => {
  try {
    const params = { companyId: null };
    const res = await getCompanyDeviceTree(params);

    if (res.success) {
      deviceList.value = res.data.list;
      deviceTreeOptions.value = res.data.list?.length
        ? formatDeviceTree(res.data.list)
        : [];
      console.log('设备树加载完成，节点数量:', deviceTreeOptions.value.length);
    } else {
      ElMessage.error(res.msg || t("message.reaTimePlot.loadDeviceTreeFailed"));
    }
  } catch (error) {
    ElMessage.error(t("message.reaTimePlot.loadDeviceTreeFailed"));
  }
};

// 从组合值中提取设备ID
const extractDeviceId = (combinedValue) => {
  if (!combinedValue) return null;
  const parts = combinedValue.split('_');
  return parts[0] ? parseInt(parts[0]) : null;
};


const handleDeviceChange = (selectedKey) => {
  console.log('设备选择变化:', selectedKey);  
  const selectedId =  extractDeviceId(selectedKey);
//  ElMessage.error(`${selectedId} handleDeviceChange`);
  ResetData();
  
  if (!selectedId) {
    selectedDevice.value = {};
    currentDeviceId.value = null;
    selectedVariables.value = [];
    return;
  }

  const findNode = (nodes, id) => {
    for (const node of nodes) {
      console.log('查找设备节点:', node.id, node.name, '目标:', id);
      if (node.id === id && node.type === 2) 
       {
        console.log('找到设备节点:', node);
        return node;
      }
      if (node.children) {
        const found = findNode(node.children, id);
        if (found) return found;
      }
    }
    return null;
  };  
  const selectedNode = findNode(deviceTreeOptions.value, selectedId);
  console.log('设备查找结果:', selectedNode);

  if (selectedNode && selectedNode.type === 2) {
    selectedDevice.value = selectedNode;
    currentDeviceId.value = selectedNode.id;
    currentDeviceKey.value = selectedNode.uniqueValue;
    console.log('设备已选择:', selectedNode.name);
  } else {
    console.warn('未找到设备节点,ID:', selectedId);
    //ElMessage.warning('选择的设备不存在');
  }
};

/**
 * 将 C# FileTime 转换为 YYYY-MM-DD hh:mm:ss 格式
 */
function fileTimeToFormat(fileTime) {
  const FILE_TIME_EPOCH_DIFF = 11644473600000;
  const jsTimestamp = fileTime / 10000 - FILE_TIME_EPOCH_DIFF;
  const date = new Date(jsTimestamp);
  if (isNaN(date.getTime())) {
    return "无效时间";
  }
  
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');
  
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

// 处理查询到的历史数据
const processHistoryData = (apiData) => {
  try {
    if (!apiData?.list) return [];
    const processedData = [];    
    apiData.list.forEach(device => {
      if (!device.list?.length) return;      
      device.list.forEach(item => {
        const dataItem = {
          timestamp: fileTimeToFormat(item.timestamp),
          rawTime: new Date(item.timestamp),
          device_code: device.device_code,
          var_name: device.var_name,
          [device.var_name]: item.var_value ?? null,
        };
        
        processedData.push(dataItem);
      });
    });

    return processedData;
  } catch (error) {
    console.error("Data processing error:", error);
    ElMessage.error(t("message.reaTimePlot.dataProcessError"));
    return [];
  }
};

// 更新缓存数据
const updateCachedData = (newData) => {
  if (!newData || newData.length === 0) return;

  newData.forEach(item => {
    // 使用时间戳作为唯一标识
    const key = item.timestamp;
    if (cachedHistoryData.value.has(key)) {
      // 存在则更新（合并数据）
      const existingItem = cachedHistoryData.value.get(key);
      // 合并变量数据
      Object.keys(item).forEach(key => {
        if (key !== 'timestamp' && key !== 'rawTime') {
          existingItem[key] = item[key];
        }
      });
    } else {
      // 不存在则插入
      cachedHistoryData.value.set(key, { ...item });
    }
  });
};

// 从缓存获取当前页数据
const getPagedDataFromCache = () => {
  const allData = Array.from(cachedHistoryData.value.values());
  
  // 按时间倒序排序
  const sortedData = allData.sort((a, b) => new Date(b.rawTime) - new Date(a.rawTime));
  
  return sortedData;
};

// 获取所有缓存数据（用于图表）
const getAllCachedData = () => {
  return Array.from(cachedHistoryData.value.values());
};

 const aggregateData = (data, maxPoints = 3000) => {
      if (data.length <= maxPoints) {
        return data;
      }

      console.log(`数据量 ${data.length} 超过 ${maxPoints}，进行聚合处理`);
      
      const aggregatedData = [];
      const interval = Math.ceil(data.length / maxPoints);
      
      for (let i = 0; i < data.length; i += interval) {
        const chunk = data.slice(i, i + interval);
        if (chunk.length === 0) continue;
        
        // 取中间点的时间戳
        const middleIndex = Math.floor(chunk.length / 2);
        const aggregatedItem = {
          ...chunk[middleIndex], // 使用中间点的数据作为基础
          timestamp: chunk[middleIndex].timestamp
        };
        
        // 对每个变量进行聚合计算
        selectedVariables.value.forEach(variable => {
          const values = chunk
            .map(item => parseFloat(item[variable.name]))
            .filter(value => !isNaN(value));
          
          if (values.length > 0) {
            // 计算平均值
            const avg = values.reduce((sum, val) => sum + val, 0) / values.length;
            aggregatedItem[variable.name] = Number(avg.toFixed(4));
          } else {
            aggregatedItem[variable.name] = null;
          }
        });
        
        aggregatedData.push(aggregatedItem);
      }
      
      console.log(`聚合后数据量: ${aggregatedData.length}`);
      return aggregatedData;
    };

const updateChart = (srcData) => {
  if (!chartInstance || !selectedVariables.value.length) return;

  const data = aggregateData(srcData, 3000);
  // 1. 按时间戳升序排序
  const sortedData = [...data].sort(
    (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
  );

  // 2. 准备图表数据
  const timeData = sortedData.map((item) => item.timestamp);
  const seriesData = selectedVariables.value.map((variable) => ({
    name: variable.display_name,
    type: "line",
    symbol: "circle",
    symbolSize: 6,
    smooth: true,
    lineStyle: {
      width: 2,
    },
    itemStyle: {
      borderWidth: 1,
      borderColor: "#fff",
    },
    data: sortedData.map((item) => {
      const value = parseFloat(item[variable.name]);
      return isNaN(value) ? null : value;
    }),
  }));

  // 3. 图表配置
  const option = {
    legend: {
      data: getDisplayNames(),
      selected: Object.fromEntries(
        selectedVariables.value.map((variable) => [variable.display_name, true])
      ),      
    },
    xAxis: {
      type: "category",
      boundaryGap: false,
      data: timeData,
      axisLabel: {
        rotate: 30,
        formatter: (value, index) =>
          index % Math.ceil(timeData.length / 10) === 0 ? value : "",
      },
    },
    yAxis: {
      type: "value",
      scale: true,
      axisLine: {
        show: true,
      },
      splitLine: {
        show: true,
        lineStyle: {
          type: "dashed",
        },
      },
    },
    series: seriesData,
    dataZoom: [
      { type: "inside", start: 0, end: 100 },
      { start: 0, end: 100 },
    ],
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "cross",
        label: {
          backgroundColor: "#6a7985",
        },
      },
    },
    grid: {
      left: "3%",
      right: "4%",
      bottom: "3%",
      containLabel: true,
    },
  };

  // 4. 应用配置
  chartInstance.setOption(option, { notMerge: true });
  setTimeout(() => chartInstance.resize(), 100);
};

// 更新表格数据
const updateTable = () => {
  // 从缓存获取当前页数据
  const pagedData = getPagedDataFromCache();
  
  tableData.value = pagedData.map((item) => {
    const row = { timestamp: item.timestamp };
    selectedVariables.value.forEach((variable) => {
      row[variable.name] = item[variable.name] ?? "--";
    });
    return row;
  });
};

// 清空图表
const clearChart = () => {
  if (chartInstance) {
    chartInstance.clear();
  }
};

const handleVariableChange = (newSelection) => {
  try {
    ResetData();
    if (newSelection.length > 10) {
      ElMessage.error(t("message.reaTimePlot.SelectVarMax"));
      return;
    }

    if (newSelection && newSelection.length > 0) {
      selectedVariables.value = newSelection;
      // 如果有缓存数据，更新图表和表格
      if (cachedHistoryData.value.size > 0) {
        updateChart(getAllCachedData());
        updateTable();
      }
    } else {
      clearChart();
      tableData.value = [];
      selectedVariables.value = [];
    }
  } catch (error) {
    ElMessage.error(`${error}`);
  }
};

// 格式化时间发送格式
const formatTimeSend = (dateString) => {
  if (!dateString) return '';
  return dateString.replace(' ', 'T');
};

// 在 methods 或 setup 中添加解压方法
const decompressData = (compressedBase64) => {
  try {
    // 将 base64 字符串转换为 Uint8Array
    const compressedData = Uint8Array.from(atob(compressedBase64), c => c.charCodeAt(0));
    
    // 使用 pako 解压
    const decompressedData = pako.inflate(compressedData, { to: 'string' });
    
    // 解析 JSON
    return JSON.parse(decompressedData);
  } catch (error) {
    console.error('数据解压失败:', error);
    throw error;
  }
};

// 确保 handleApiResponse 函数正确定义
const handleApiResponse = async (response) => {
  // 如果 response 已经是解析后的对象
  if (response.data && response.success !== undefined) {
    const result = response;    
    if (result.success && result.data.compressed) {
      try {
        const decompressedList = decompressData(result.data.compressedData);
        return {
          ...result,
          data: {
            ...result.data,
            list: decompressedList,
            compressed: false
          }
        };
      } catch (error) {
        console.error('数据解压失败:', error);
        throw new Error('数据解压失败');
      }
    }
    return result;
  }
  
  // 如果 response 是原始响应，需要先解析 JSON
  const result = await response.json();
  
  if (result.success && result.data.compressed) {
    try {
      const decompressedList = decompressData(result.data.compressedData);
      return {
        ...result,
        data: {
          ...result.data,
          list: decompressedList,
          compressed: false
        }
      };
    } catch (error) {
      console.error('数据解压失败:', error);
      throw new Error('数据解压失败');
    }
  }
  return result;
};

const splitTimeRangeToDays = (startTime, endTime) => {
  const ranges = [];
  const start = new Date(startTime);
  const end = new Date(endTime);
  
  if (end - start <= 24 * 60 * 60 * 1000) {
    ranges.push({
      start: formatLocalDateTime(start),
      end: formatLocalDateTime(end),
      date: start.toISOString().split('T')[0]
    });
    return ranges;
  }
  
  let currentDay = new Date(start);
  currentDay.setHours(0, 0, 0, 0);
  
  while (currentDay <= end) {
    const dayStart = new Date(currentDay);
    const dayEnd = new Date(currentDay);
    dayEnd.setHours(23, 59, 59, 999);
    
    const actualStart = dayStart < start ? start : dayStart;
    const actualEnd = dayEnd > end ? end : dayEnd;
    
    if (actualStart <= actualEnd) {
      ranges.push({
        start: formatLocalDateTime(actualStart),
        end: formatLocalDateTime(actualEnd),
        date: currentDay.toISOString().split('T')[0]
      });
    }
    
    currentDay.setDate(currentDay.getDate() + 1);
  }
  
  return ranges;
};

// 方案1：更加合理的动态计算
const calculateMaxPointsPerDay = (totalDays) => {
  if (totalDays <= 1) return 2000;      // 1天：2000点（高精度）
  if (totalDays <= 3) return 1500;      // 2-3天：1500点
  if (totalDays <= 8) return 1000;      // 4-7天：1000点
  if (totalDays <= 15) return 500;      // 8-14天：500点
  if (totalDays <= 30) return 300;      // 15-30天：300点
  return 200;                           // >30天：200点
};


// 获取设备变量名映射
const getDeviceVariableMap = () => {
 
    // 直接构建后端期望的格式
    const var_names = selectedVariables.value.map(varObj => ({
      var_name: varObj.name,
      mult: varObj.mult,
      var_decimal:varObj.var_decimal
    }));
  return var_names;
};

const fetchHistoryData = async () => {
  loading.value = true;
  
  try {
    const queryPromises = [];
    const var_names = getDeviceVariableMap();
    const dailyTimeRanges = splitTimeRangeToDays(timeRange.value[0], timeRange.value[1]);
    
    const maxPointsPerDay = calculateMaxPointsPerDay(dailyTimeRanges.length);
  

    for (const dailyRange of dailyTimeRanges) {
      const params = {
        lang: localStorage.getItem('lang') || 'zh-CN',
        company_id: selectedDevice.value.parentId,
        device_code:  selectedDevice.value.deviceCode,
        slave_id: undefined,
        maxPoints: maxPointsPerDay,
        var_names: var_names,          
        ben_time: formatTimeSend(dailyRange.start),
        end_time: formatTimeSend(dailyRange.end),
        pageNum: pagination.currentPage,        
        pageSize: pagination.pageSize,
      };
      
      queryPromises.push(
        queryHistoryWhenAllData(params).then(async(res) => {
          if (res.success) {
            
            const resConvert = await handleApiResponse(res); // 添加 await
            return processHistoryData(resConvert.data);
          } else {            
            return [];
          }
        }).catch(error => {
          console.error(`设备 ${selectedDevice.value.deviceCode} ${dailyRange.start} 查询异常:`, error);
          return [];
        })
      );
    }
   
    const results = await Promise.all(queryPromises);
    const allData = results.flat();
     
    // 更新缓存数据
    updateCachedData(allData);
    
    // 更新分页总数（只在第一页时更新）
    if (pagination.currentPage <= 1) {
      pagination.total = allData.length;
    }
    
    // 从缓存更新图表和表格
    updateChart(getAllCachedData());
    updateTable();

    if (allData.length === 0 && pagination.currentPage === 1) {
      ElMessage.info(t("message.historyRecord.noData"));
    } else {
      ElMessage.success(`查询到 ${allData.length} 条历史数据，共 ${dailyTimeRanges.length} 天`);
    }
  } catch (error) {
    console.error("查询历史数据失败:", error);
    ElMessage.error(t("message.historyRecord.queryError"));
  } finally {
    loading.value = false;
  }
};

 

// const fetchHistoryData = async () => {
//   loading.value = true;
  
//   try {
//     // 按天拆分时间范围
//     const dailyTimeRanges = splitTimeRangeToDays(timeRange.value[0], timeRange.value[1]);
    
//     //ElMessage.error(`${selectedDevice.value.parentId}-${selectedDevice.value.deviceCode} fetchHistoryData`);

//     // 并行查询所有天数
//     const results = await Promise.all(
//       dailyTimeRanges.map(async (dailyRange) => {
//         try {
//           const params = {
//             lang: localStorage.getItem('lang') || 'zh-CN',
//             company_id: selectedDevice.value.parentId,      
//             device_code: selectedDevice.value.deviceCode,
//             slave_id: undefined,
//             var_names: getVariableNames(),
//             ben_time: formatTimeSend(dailyRange.start),
//             end_time: formatTimeSend(dailyRange.end),
//             pageNum: pagination.currentPage,        
//             pageSize: pagination.pageSize,
//           };
 

//           const res = await queryHistoryAllData(params);
          
//           if (res.success) {
//             const resConvert = await handleApiResponse(res);
//             return processHistoryData(resConvert.data);
//           } else {
//             console.error(`日期 ${dailyRange.date} 查询失败:`, res.msg);
//             return [];
//           }
//         } catch (error) {
//           console.error(`日期 ${dailyRange.date} 查询异常:`, error);
//           return [];
//         }
//       })
//     );
    
//     // 合并所有数据
//     const allData = results.flat();
    
//     // 更新缓存数据
//     updateCachedData(allData);
    
//     // 更新分页总数（只在第一页时更新）
//     if (pagination.currentPage <= 1) {
//       pagination.total = allData.length;
//     }
    
//     // 从缓存更新图表和表格
//     updateChart(getAllCachedData());
//     updateTable();
    
//     // 显示查询结果信息
//     if (allData.length === 0 && pagination.currentPage === 1) {
//       ElMessage.info(t("message.historyRecord.noData"));
//     } else {
//      // const successDays = results.filter(dayData => dayData.length > 0).length;
//      // ElMessage.success(`查询到 ${allData.length} 条历史数据，${successDays}/${dailyTimeRanges.length} 天成功`);
//     }
//   } catch (error) {
//     console.error("查询历史数据失败:", error);
//     ElMessage.error(t("message.historyRecord.queryError"));
//   } finally {
//     loading.value = false;
//   }
// };

const handleExport = async () => {
  if (!currentDeviceId.value) {
    ElMessage.error(t("message.varableSelectorControl.selectDeviceFirst"));
    return;
  }

  if (selectedVariables.value.length === 0) {
    ElMessage.error(t("message.varableSelectorControl.selectVariableFirst"));
    return;
  }

  if (!timeRange.value || timeRange.value.length !== 2) {
    ElMessage.error(t("message.historyRecord.selectTimeRange"));
    return;
  }
  cachedHistoryData.value.clear();
  const startTime = new Date(timeRange.value[0]);
  const endTime = new Date(timeRange.value[1]);
  const diffInMs = endTime - startTime;
  const hours = Math.floor(diffInMs / (1000 * 60 * 60));  
  if(hours > 192) {
    ElMessage.error(t("message.reaTimePlot.exceed", {val: 192}));
    return;
  }

  // 重置到第一页
  pagination.currentPage = 1;
  exportLoading.value = true
  await fetchHistoryDataExport();
  exportLoading.value = false
};




const fetchHistoryDataExport = async () => {
  try {

    const exportPromises = []; 
    const dailyTimeRanges = splitTimeRangeToDays(timeRange.value[0], timeRange.value[1]);

    const var_names = getDeviceVariableMap();   
    
        // 获取当前选中设备的变量
    const deviceCode = selectedDevice.value.deviceCode;
    const companyId = selectedDevice.value.parentId;
     

      for (const dailyRange of dailyTimeRanges) {
        const params = {
          lang: localStorage.getItem('lang') || 'zh-CN',
        company_id: companyId,
        device_code:  deviceCode,
          slave_id: undefined,
          var_names: var_names,
          ben_time: formatTimeSend(dailyRange.start),
          end_time: formatTimeSend(dailyRange.end),
          pageNum: 1,        
          pageSize: 10000,
        };
            
        exportPromises.push(
          exportHisVariablesByFile(params).then(response => {
            return { 
              deviceCode, 
              response, 
              date: dailyRange.date
            };
          }).catch(error => {
            console.error(`设备 ${deviceCode} ${dailyRange.date} 导出异常:`, error);
            return { 
              deviceCode, 
              error: true, 
              date: dailyRange.date,
              message: error.message 
            };
          })
        );
      }
    
    
    const results = await Promise.all(exportPromises);
    const exportResults = new Map();
    let successCount = 0;
    let failCount = 0;
    
    for (const result of results) {
      if (result.error) {
        failCount++;
        continue;
      }
      
      const { deviceCode, response, date } = result;
      
      if (!(response.data instanceof Blob)) {
        failCount++;
        continue;
      }
      
      if (!exportResults.has(deviceCode)) {
        exportResults.set(deviceCode, []);
      }
      
      exportResults.get(deviceCode).push({
        blob: response.data,
        date: date
      });
      
      successCount++;
    }
    
    for (const [deviceCode, dailyExports] of exportResults) {
      for (const exportData of dailyExports) {
        downloadExportFile(exportData.blob, deviceCode, exportData.date);
      }
    }
    
    if (successCount > 0) {
      ElMessage.success(`成功导出 ${successCount} 个数据文件，失败 ${failCount} 个`);
    } else {
      ElMessage.error('导出失败，请检查网络连接或联系管理员');
    }

  } catch (error) {
    console.error("导出历史数据失败:", error);
    ElMessage.error(t("message.historyRecord.exportError"));
  }
};


const downloadExportFile = (blobData, deviceCode, date) => {
  const blob = new Blob([blobData], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  
  const fileName = `HistoryData_${deviceCode}_${date}.xlsx`;  
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  
  setTimeout(() => {
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, 100);
};

// const fetchHistoryDataExport = async () => {
//   loading.value = true;
  
//   try {
//     const params = {
//       lang: localStorage.getItem('lang') || 'zh-CN',
//       company_id: selectedDevice.value.parentId,      
//       device_code: selectedDevice.value.deviceCode,
//       slave_id: undefined,
//       var_names: getVariableNames(),
//       ben_time: formatTimeSend(timeRange.value[0]),
//       end_time: formatTimeSend(timeRange.value[1]),
//       pageNum: pagination.currentPage,        
//       pageSize: pagination.pageSize,
//     };

  
//     exportLoading.value = true
//     const response = await exportHisVariablesByFile(params)
    
//     // 确保响应是Blob类型
//     if (!(response.data instanceof Blob)) {
//       ElMessage.error(t('message.historyRecord.exportDataError')) 
//       return;
//     }
    
//     // 创建Blob对象
//     const blob = new Blob([response.data], {
//       type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
//     })
    
//     // 创建下载链接
//     const url = window.URL.createObjectURL(blob)
//     const link = document.createElement('a')
//     link.href = url
    
//     // 从响应头获取文件名
//     let fileName = `HistoryDataExport_${new Date().toISOString().slice(0, 100)}.xlsx`
//     const contentDisposition = response.headers['content-disposition']
    
//     if (contentDisposition) {
//       const fileNameMatch = contentDisposition.match(/filename\*?=['"]?(?:UTF-\d['"]*)?([^;\r\n"']*)['"]?;?/i)
//       if (fileNameMatch && fileNameMatch.length > 1) {
//         fileName = decodeURIComponent(fileNameMatch[1])
//       }
//     }
    
//     link.setAttribute('download', fileName)
//     document.body.appendChild(link)
//     link.click()
    
//     // 清理
//     setTimeout(() => {
//       document.body.removeChild(link)
//       window.URL.revokeObjectURL(url)
//     }, 60000)
    
//     ElMessage.success(t('message.historyRecord.exportSuccess'))

//   } catch (error) {
//     console.error("查询历史数据失败:", error);
//     ElMessage.error(t("message.historyRecord.queryError"));
//   } finally {
//     loading.value = false;
//   }
// };

// 查询历史数据
const handleQuery = async () => {
  
  if (!currentDeviceId.value) {
    ElMessage.error(t("message.varableSelectorControl.selectDeviceFirst"));
    return;
  }

  if (selectedVariables.value.length === 0) {
    ElMessage.error(t("message.varableSelectorControl.selectVariableFirst"));
    return;
  }

  if (!timeRange.value || timeRange.value.length !== 2) {
    ElMessage.error(t("message.historyRecord.selectTimeRange"));
    return;
  }
  
  // 保存选择信息
  saveSelectionToStorage();
  // 清理过期存储
  cleanupExpiredStorage();
  
  cachedHistoryData.value.clear();
  const startTime = new Date(timeRange.value[0]);
  const endTime = new Date(timeRange.value[1]);
  const diffInMs = endTime - startTime;
  const hours = Math.floor(diffInMs / (1000 * 60 * 60));  
  if(hours > 192) {
    ElMessage.error(t("message.reaTimePlot.exceed", {val: 192}));
    return;
  }

  // 重置到第一页
  pagination.currentPage = 1;
  await fetchHistoryData();
};

// 分页大小改变
const handleSizeChange =async (newSize) => {
  pagination.pageSize = newSize;
  pagination.currentPage = 1;
  await fetchHistoryData();
  if (cachedHistoryData.value.size > 0) {
    // 从缓存更新表格
    updateTable();
  }
};

// 当前页改变
const handleCurrentChange =async (newPage) => {
  pagination.currentPage = newPage;
  await fetchHistoryData();
  if (cachedHistoryData.value.size > 0) {
    // 从缓存更新表格
    updateTable();
  }
};

const ResetData = () => {  
  // 清空缓存数据
  cachedHistoryData.value.clear();
  tableData.value = [];
  pagination.total = 0;
  pagination.currentPage = 1;  
  clearChart();
};

// 重置查询条件
const handleReset = () => {
  timeRange.value = [];
  selectedVariables.value = [];
  // 清空缓存数据
  cachedHistoryData.value.clear();
  tableData.value = [];
  pagination.total = 0;
  pagination.currentPage = 1;
  quickTime.value = 'today'; // 重置时也默认选择今天
  
  // 清空保存的选择信息
  clearSavedSelection();
  
  // 重新初始化时间范围
  initializeTimeRange();
  clearChart();
};

// 生命周期钩子
onMounted(async () => {
  console.log('组件挂载开始');
  loadOptions();
  initChart();
  
  // 先加载保存的选择信息（除了设备）
  loadSelectionFromStorage();
  
  // 然后加载设备树
  console.log('开始加载设备树');
  await loadDeviceTree();
  
  console.log('组件挂载完成');
  
  // 如果没有保存的时间范围，使用默认值
  if (!timeRange.value || timeRange.value.length === 0) {
    initializeTimeRange();
  }
});

// 监听设备树加载完成，然后设置设备选择
watch(deviceTreeOptions, (newOptions) => {
  if (newOptions && newOptions.length > 0 && currentDeviceId.value) {
    console.log('设备树加载完成，开始设置存储的设备:', currentDeviceId.value);
    // 设备树加载完成后，设置存储的设备
    setDeviceFromStorage(currentDeviceId.value);
  }
}, { deep: true });

onBeforeUnmount(() => {
  if (chartInstance) {
    chartInstance.dispose();
  }
});
</script>

<style scoped>
.historyrecord-container {
  padding: 12px;
  background-color: #f5f7fa;
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 16px;
  box-sizing: border-box;
}


.chart-container {
  background: #fff;
  padding: 12px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
  height: 40%;
}

.chart {
  width: 100%;
  height: 100%;
}

.control-panel {
  height: 70%;
  background: #fff;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  gap: 16px;
}

.filter-section {
  flex-shrink: 0;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 1px; /* 减小间距 */
}

.filter-item {
  margin-bottom: 0;
  min-width: 200px;
  flex: 1;
  margin-right: 0; /* 确保没有额外的右边距 */
}

/* 统一控制元素样式 */
.filter-control {
  flex: 1;
  min-width: 180px;
}

/* 时间范围组样式 */
.time-range-item {
  min-width: 400px;
}

.time-range-group {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.quick-time-select {
  width: 120px;
  flex-shrink: 0;
}

.time-range-picker {
  flex: 1;
  min-width: 320px;
}

.action-buttons {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  margin-left: auto; /* 让按钮组靠右 */
}

.action-btn {
  flex-shrink: 0;
}

 
.data-section {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  gap: 16px;
}

.table-container {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.data-table {
  width: 100%;
  height: 100%;
}

.pagination-container {
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  padding: 16px 0;
  border-top: 1px solid #ebeef5;
}

.pagination {
  width: 100%;
  justify-content: center;
}

/* 变量选择器高度调整 */
.variable-selector:deep(.selector-header) {
  height: 32px;
  min-height: 32px;
  line-height: 32px;
  padding: 0 12px;
  display: flex;
  align-items: center;
}

/* 变量选择器下拉面板 */
.variable-selector:deep(.selector-dropdown) {
  top: 100% !important;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .filter-row {
    gap: 5px;
  }
  
  .filter-item {
    min-width: 180px;
    flex: 0 1 calc(33.333% - 1px); /* 三个项目平均分配宽度 */
  }
  
  .time-range-item {
    min-width: 350px;
  }
  
  .time-range-picker {
    min-width: 280px;
  }

  .action-buttons {
    margin-left: 0; /* 在小屏幕上取消靠右 */
    width: 100%;
    justify-content: flex-start;
  }
}

@media (max-width: 992px) {
  .filter-item {
    flex: 0 1 calc(50% - 8px); /* 两个项目一行 */
  }

  .time-range-item {
    min-width: auto;
  }
}

@media (max-width: 768px) {
  .historyrecord-container {
    padding: 8px;
    gap: 12px;
  }
  
  .chart-container {
    height: 300px;
    padding: 8px;
  }
  
  .control-panel {
    padding: 12px;
    gap: 12px;
  }
  
  .filter-item {
    flex: 0 1 100%; /* 一个项目一行 */
    min-width: auto;
  }
  
  .time-range-item {
    min-width: auto;
  }
  
  .time-range-group {
    flex-direction: column;
    gap: 8px;
  }
  
  .quick-time-select,
  .time-range-picker {
    width: 100%;
    min-width: auto;
  }
  
  .action-buttons {
    width: 100%;
    justify-content: space-between;
  }
  
  .action-btn {
    flex: 1;
    min-width: auto;
  }
  
  .filter-row {
    gap: 5px;
  }
}

@media (max-width: 480px) {
  .historyrecord-container {
    padding: 4px;
    gap: 8px;
  }
  
  .chart-container {
    height: 250px;
    padding: 4px;
  }
  
  .control-panel {
    padding: 8px;
    gap: 8px;
  }
  
  .action-buttons {
    flex-direction: column;
  }
  
  .action-btn {
    width: 100%;
  }
}

/* 滚动条样式优化 */
:deep(.el-table__body-wrapper)::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

:deep(.el-table__body-wrapper)::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

:deep(.el-table__body-wrapper)::-webkit-scrollbar-thumb {
  background: #c0c4cc;
  border-radius: 3px;
}

:deep(.el-table__body-wrapper)::-webkit-scrollbar-thumb:hover {
  background: #909399;
}

/* 表格样式优化 */
:deep(.el-table) {
  font-size: 14px;
}

:deep(.el-table .el-table__cell) {
  padding: 8px 0;
}

:deep(.el-table .cell) {
  padding: 0 12px;
  line-height: 1.4;
}

/* 表单样式优化 */
:deep(.el-form-item__label) {
  font-weight: 500;
}

:deep(.el-cascader .el-input__inner) {
  height: 32px;
  line-height: 30px;
}

/* 加载状态优化 */
:deep(.el-loading-mask) {
  background-color: rgba(255, 255, 255, 0.8);
}
</style>